/// <amd-module name="Feed/Legacy/Protocols/NuGet/NuGetOverviewAttributesPanel" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssFeatureService } from "VSS/Platform/Feature";

import * as PackageResources from "Feed/Common/Resources";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { PackageAttribute } from "Feed/Legacy/Protocols/Components/PackageAttribute";
import { PackageDependencies } from "Feed/Legacy/Protocols/Components/PackageDependencies";
import { IProjectAttribute, PackageDevelopment } from "Feed/Legacy/Protocols/Components/PackageDevelopment";
import { PackagePeople } from "Feed/Legacy/Protocols/Components/PackagePeople";
import { PackageProvenance } from "Feed/Legacy/Protocols/Components/PackageProvenance";
import { PackageStats } from "Feed/Legacy/Protocols/Components/PackageStats";
import { PackageTags } from "Feed/Legacy/Protocols/Components/PackageTags";
import { NuGetCiConstants } from "Feed/Legacy/Protocols/NuGet/Constants/NuGetConstants";
import { NuGetDependencyHelper } from "Feed/Legacy/Protocols/NuGet/NuGetDependencyHelper";
import { INuGetProtocolMetadata } from "Feed/Legacy/Protocols/NuGet/NuGetProtocolMetadata";
import { INuGetRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/NuGet/NuGetRestApiUrlGeneratorService";

export class NuGetOverviewAttributesPanel extends React.PureComponent<IPackageDetailsProps> {
    public render(): JSX.Element {
        const nugetMetadata = this._getNuGetMetadata();

        const projectAttributes: IProjectAttribute[] = [];

        if (nugetMetadata) {
            if (nugetMetadata.projectUrl) {
                projectAttributes.push({
                    iconClass: "bowtie-icon bowtie-globe",
                    text: PackageResources.NuGetLinks_Project,
                    url: nugetMetadata.projectUrl,
                    onClickCiFeature: NuGetCiConstants.ProjectLinkClicked
                } as IProjectAttribute);
            }

            const licenseInfo = this._getLicenseInfo();
            if (licenseInfo) {
                projectAttributes.push({
                    iconClass: "bowtie-icon bowtie-script",
                    text: licenseInfo.title,
                    url: licenseInfo.url,
                    onClickCiFeature: NuGetCiConstants.LicenseLinkClicked
                } as IProjectAttribute);
            }
        }

        return (
            <div className="nuget-overview-attributes-panel">
                <PackageProvenance
                    pageContext={this.props.pageContext}
                    feed={this.props.feed}
                    packageId={this.props.packageSummary.id}
                    versionId={this.props.packageVersion.id}
                    publishDate={new Date(this.props.packageVersion.publishDate)}
                    isProvenanceEnabled={this.props.isProvenanceEnabled}
                    isProvenanceSupported={true}
                />
                {this.props.packageVersion.author != null && (
                    <PackagePeople authors={this.props.packageVersion.author.split(",")} />
                )}
                <PackageDevelopment
                    pageContext={this.props.pageContext}
                    projectAttributes={projectAttributes}
                    packageSummary={this.props.packageSummary}
                    packageVersion={this.props.packageVersion}
                    upstreamSources={this.props.feed.project ? null : this.props.feed.upstreamSources}
                />
                {this.props.isMetricsEnabled && (
                    <PackageStats
                        packageVersionMetrics={this.props.packageVersionMetrics}
                        packageMetrics={this.props.packageMetrics}
                        isMetricsLoading={this.props.isMetricsLoading}
                        feed={this.props.feed}
                    />
                )}
                <PackageTags tags={this.props.packageVersion.tags} />
                <PackageDependencies
                    pageContext={this.props.pageContext}
                    feed={this.props.feed}
                    packageSummary={this.props.packageSummary}
                    packageVersion={this.props.packageVersion}
                    dependencyHelperCallback={NuGetDependencyHelper.formatVersionRanges}
                    isSmartDependenciesEnabled={this.props.isSmartDependenciesEnabled}
                />
                {nugetMetadata != null && (
                    <div>
                        <PackageAttribute title={PackageResources.NuGetMinimumClientVersion_Title}>
                            {nugetMetadata.minClientVersion}
                        </PackageAttribute>
                        <PackageAttribute title={PackageResources.NuGetCopyright_Title}>
                            {nugetMetadata.copyright}
                        </PackageAttribute>
                        <PackageAttribute title={PackageResources.NuGetLanguage_Title}>
                            {nugetMetadata.language}
                        </PackageAttribute>
                    </div>
                )}
            </div>
        );
    }

    private _getLicenseInfo(): { title: string, url: string | null } | null {
        const nuGetMetadata = this._getNuGetMetadata();
        if (!nuGetMetadata) {
            return null;
        }

        const featureService = this.props.pageContext.getService<IVssFeatureService>("IVssFeatureService");
        if (nuGetMetadata.licenseFile
            && featureService.isFeatureFlagEnabled("Packaging.Feed.NuGet.EmbeddedLicenseLink", false)) {
            // when removing the above Packaging.Feed.NuGet.EmbeddedFilesUI check, be sure to also remove
            // the fallback below

            const generator = this.props.pageContext.getService<INuGetRestApiUrlGeneratorService>("INuGetRestApiUrlGeneratorService");
            return {
                title: PackageResources.NuGetLinks_License,
                url: generator.getExtractFromNupkgUrl(
                    this.props.feed.id,
                    this.props.packageSummary.normalizedName,
                    this.props.packageVersion.normalizedVersion,
                    nuGetMetadata.licenseFile,
                    !!this.props.feed.project /* includeProject */)
            };
        }
        
        if (nuGetMetadata.licenseExpression) {
            return {
                title: Utils_String.format(PackageResources.NuGetLinks_LicenseExpression, nuGetMetadata.licenseExpression),
                url: `https://licenses.nuget.org/${encodeURIComponent(nuGetMetadata.licenseExpression)}`
            };
        }
        
        if (nuGetMetadata.licenseUrl) {
            return {
                title: PackageResources.NuGetLinks_License,
                url: nuGetMetadata.licenseUrl
            };
        }

        if (nuGetMetadata.licenseFile) {
            // fallback (remove this when removing the FF check)
            // If there's an embedded license and neither an expression nor a url, but the FF is off, at least alert
            // the user to the fact that there is a license file embedded
            return {
                title: Utils_String.format(PackageResources.NuGetLinks_LicenseEmbedded, nuGetMetadata.licenseFile),
                url: null
            };
        }

        return null;
    }

    private _getNuGetMetadata() : INuGetProtocolMetadata {
        return this.props.packageVersion.protocolMetadata && this.props.packageVersion.protocolMetadata.data
                ? (this.props.packageVersion.protocolMetadata.data as INuGetProtocolMetadata)
                : null;
    }
}
